// simple date picker for use within Zion form
// derived from FullnameFields.js in zion-ui
// uses first month at 1 (not zero)
import React, { useMemo } from 'react'
import { listOfDays, listOfMonths, listOfYears } from '@fs/zion-locale'
import { FormInputGroup, FormSelect } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { Row } from '@fs/zion-ui'
import { getDayMonthYearOrder } from '@fs/identity.js'

const currentYear = new Date().getFullYear()
const minYearsSupported = 8
const maxYearsSupported = 149

const calculateDateOptions = () => ({
  dayOptions: listOfDays().map((day) => ({
    value: day,
    label: day,
  })),
  monthOptions: listOfMonths().map((month, index) => ({
    value: String(index + 1),
    label: month,
  })),
  yearOptions: listOfYears(currentYear - minYearsSupported, currentYear - maxYearsSupported).map((year) => ({
    value: year,
    label: year,
  })),
})

export default function DatePicker({ label, disabled, dayName = 'day', monthName = 'month', yearName = 'year' }) {
  const { t, i18n } = useTranslation()
  const dayLabel = t('common-ui:day.label', 'Day')
  const monthLabel = t('common-ui:month.label', 'Month')
  const yearLabel = t('common-ui:year.label', 'Year')
  const { dayOptions, monthOptions, yearOptions } = useMemo(() => calculateDateOptions(i18n.language), [i18n.language])
  const dayMonthYearOrder = useMemo(() => getDayMonthYearOrder(i18n.language), [i18n.language])
  const selectProps = {
    growX: true,
    disabled,
  }
  const selects = {
    day: (
      <FormSelect
        id={dayName}
        key={dayName}
        name={dayName}
        aria-label={dayLabel}
        placeholder={dayLabel}
        options={dayOptions}
        {...selectProps}
      />
    ),
    month: (
      <FormSelect
        id={monthName}
        key={monthName}
        name={monthName}
        aria-label={monthLabel}
        placeholder={monthLabel}
        options={monthOptions}
        {...selectProps}
      />
    ),
    year: (
      <FormSelect
        id={yearName}
        key={yearName}
        name={yearName}
        aria-label={yearLabel}
        placeholder={yearLabel}
        options={yearOptions}
        {...selectProps}
      />
    ),
  }
  const orderedSelects = dayMonthYearOrder.map((x) => selects[x])
  return (
    <FormInputGroup label={label} disabled={disabled}>
      <Row wrap gutters="xxs">
        {orderedSelects}
      </Row>
    </FormInputGroup>
  )
}
